import { useEffect, useRef, useState, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTutorial } from "../context/TutorialContext"

// ─── Mascot SVG ───────────────────────────────────────────────────────────────
// Remi the owl — RecordLOGS guide character

function Remi({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      {/* Body */}
      <ellipse cx="50" cy="63" rx="26" ry="30" fill="#059669" />
      {/* Head */}
      <circle cx="50" cy="36" r="27" fill="#10b981" />
      {/* Ear tufts */}
      <polygon points="30,14 23,2 38,9" fill="#047857" />
      <polygon points="70,14 77,2 62,9" fill="#047857" />
      {/* Face plate */}
      <ellipse cx="50" cy="38" rx="19" ry="18" fill="#d1fae5" />
      {/* Left eye */}
      <circle cx="38" cy="34" r="8" fill="white" />
      <circle cx="38" cy="34" r="4.5" fill="#111827" />
      <circle cx="40" cy="32" r="1.8" fill="white" />
      {/* Right eye */}
      <circle cx="62" cy="34" r="8" fill="white" />
      <circle cx="62" cy="34" r="4.5" fill="#111827" />
      <circle cx="64" cy="32" r="1.8" fill="white" />
      {/* Beak */}
      <polygon points="50,45 44,54 56,54" fill="#f59e0b" />
      {/* Belly fluff */}
      <ellipse cx="50" cy="63" rx="14" ry="18" fill="#6ee7b7" />
      {/* Wings */}
      <ellipse cx="19" cy="64" rx="9" ry="19" fill="#047857" transform="rotate(-18 19 64)" />
      <ellipse cx="81" cy="64" rx="9" ry="19" fill="#047857" transform="rotate(18 81 64)" />
      {/* Left foot */}
      <line x1="37" y1="91" x2="30" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      <line x1="37" y1="91" x2="37" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      <line x1="37" y1="91" x2="44" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      {/* Right foot */}
      <line x1="63" y1="91" x2="56" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      <line x1="63" y1="91" x2="63" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      <line x1="63" y1="91" x2="70" y2="99" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

// ─── Step progress dots ───────────────────────────────────────────────────────

function ProgressDots({ steps, currentIndex }) {
  // Only show dots for steps that have a phase (skip "wait" and "done")
  const visible = steps.filter(s => s.phase !== null && s.type !== "wait")
  const current = steps[currentIndex]
  const currentVisible = visible.findIndex(s => s.id === current?.id)

  if (visible.length <= 1) return null

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((s, i) => (
        <div
          key={s.id}
          className={`rounded-full transition-all duration-300 ${
            i === currentVisible
              ? "w-3.5 h-1.5 bg-emerald-500"
              : i < currentVisible
              ? "w-1.5 h-1.5 bg-emerald-300"
              : "w-1.5 h-1.5 bg-gray-200"
          }`}
        />
      ))}
    </div>
  )
}

// ─── Tooltip card ─────────────────────────────────────────────────────────────

function Tooltip({ step, steps, stepIndex, rect, onNext, onPrev, onSkip, navigating }) {
  const VP_PAD = 12   // min gap to viewport edge
  const TIP_W  = 320
  const TIP_H_EST = 220 // rough estimate for placement

  let style = {}

  if (rect) {
    // Decide whether to place tooltip above or below
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    if (spaceBelow >= TIP_H_EST || spaceBelow >= spaceAbove) {
      // below
      style.top  = Math.min(rect.bottom + 14, window.innerHeight - TIP_H_EST - VP_PAD)
      style.left = Math.max(VP_PAD, Math.min(rect.left, window.innerWidth - TIP_W - VP_PAD))
    } else {
      // above
      style.bottom = window.innerHeight - rect.top + 14
      style.left   = Math.max(VP_PAD, Math.min(rect.left, window.innerWidth - TIP_W - VP_PAD))
    }
  } else {
    // Centre of screen
    style.top  = "50%"
    style.left = "50%"
    style.transform = "translate(-50%, -50%)"
  }

  const isDone    = step.type === "done"
  const isAction  = step.type === "action"
  const isNav     = step.type === "navigate"
  const showNext  = !isAction  // action steps are advanced by clicking the target
  const showPrev  = stepIndex > 0 && step.type !== "done"

  return (
    <div
      className="fixed z-[10001] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
      style={{ width: TIP_W, ...style }}
    >
      {/* Top accent stripe */}
      <div className="h-1 bg-gradient-to-r from-emerald-400 to-green-500" />

      <div className="p-4">
        {/* Mascot + phase/progress row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0">
              <Remi size={48} />
            </div>
            <div>
              {step.phase && (
                <span className="inline-block text-[9px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 mb-1 leading-none">
                  {step.phase}
                </span>
              )}
              <ProgressDots steps={steps} currentIndex={stepIndex} />
            </div>
          </div>

          <button
            onClick={onSkip}
            className="text-gray-300 hover:text-gray-500 transition-colors text-xs leading-none mt-1 shrink-0"
            title="Exit tutorial"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <h3 className="text-[15px] font-bold text-gray-900 mb-1.5 leading-snug">
          {step.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          {step.body}
        </p>

        {/* Action hint for action steps */}
        {isAction && !rect && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Waiting for the element to appear...
          </p>
        )}
        {isAction && rect && (
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5">
            <span>👆</span> Tap the highlighted area to continue
          </p>
        )}

        {/* Buttons */}
        <div className="flex items-center justify-between gap-2">
          <div>
            {showPrev && (
              <button
                onClick={onPrev}
                className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isDone && (
              <button
                onClick={onSkip}
                className="text-xs text-gray-400 hover:text-gray-500 transition-colors"
              >
                Skip guide
              </button>
            )}
            {showNext && (
              <button
                onClick={onNext}
                disabled={navigating}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
              >
                {navigating ? "Going..." : (isDone ? step.next : isNav ? step.next : (step.next ?? "Next →"))}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Dark panels — cover everything except the target rect ────────────────────

function DarkPanels({ rect }) {
  const ALPHA = "rgba(0,0,0,0.72)"
  const PAD   = 5

  if (!rect) {
    // Full-screen block
    return (
      <div
        className="fixed inset-0 z-[9998]"
        style={{ background: ALPHA }}
      />
    )
  }

  const t = Math.max(0, rect.top    - PAD)
  const l = Math.max(0, rect.left   - PAD)
  const b = Math.min(window.innerHeight, rect.bottom + PAD)
  const r = Math.min(window.innerWidth,  rect.right  + PAD)
  const W = window.innerWidth
  const H = window.innerHeight

  const base = { position: "fixed", zIndex: 9998, background: ALPHA }

  return (
    <>
      {/* Top */}
      <div style={{ ...base, top: 0,   left: 0, width: W, height: t }} />
      {/* Bottom */}
      <div style={{ ...base, top: b,   left: 0, width: W, height: Math.max(0, H - b) }} />
      {/* Left */}
      <div style={{ ...base, top: t,   left: 0, width: l, height: b - t }} />
      {/* Right */}
      <div style={{ ...base, top: t,   left: r, width: Math.max(0, W - r), height: b - t }} />
    </>
  )
}

// ─── Spotlight ring ───────────────────────────────────────────────────────────

function SpotlightRing({ rect }) {
  if (!rect) return null
  const PAD = 5
  return (
    <div
      className="fixed pointer-events-none z-[9999] rounded-lg"
      style={{
        top:    rect.top    - PAD,
        left:   rect.left   - PAD,
        width:  rect.width  + PAD * 2,
        height: rect.height + PAD * 2,
        boxShadow: "0 0 0 3px rgba(52,211,153,0.9), 0 0 20px 4px rgba(52,211,153,0.3)",
        animation: "tutorialPulse 1.8s ease-in-out infinite",
      }}
    />
  )
}

// ─── Main overlay component ───────────────────────────────────────────────────

export default function TutorialOverlay() {
  const { isActive, active, currentStep, steps, nextStep, prevStep, endTutorial } = useTutorial()
  const navigate  = useNavigate()
  const location  = useLocation()
  const [rect, setRect]         = useState(null)
  const [targetEl, setTargetEl] = useState(null)
  const [navigating, setNavigating] = useState(false)
  const pollRef    = useRef(null)
  const cleanupEl  = useRef(null)
  // Always-current refs so stale effect closures never call outdated callbacks
  const nextStepRef  = useRef(nextStep)
  const endTutorialRef = useRef(endTutorial)
  useEffect(() => { nextStepRef.current = nextStep }, [nextStep])
  useEffect(() => { endTutorialRef.current = endTutorial }, [endTutorial])

  // ── Find target element, poll until it appears ──────────────────────────────
  useEffect(() => {
    if (pollRef.current) clearTimeout(pollRef.current)
    setRect(null)
    setTargetEl(null)

    if (!isActive || !currentStep?.target) return

    let cancelled = false
    let attempts  = 0

    function tryFind() {
      if (cancelled) return
      const el = document.querySelector(currentStep.target)
      if (el) {
        setTargetEl(el)
        setRect(el.getBoundingClientRect())

        // "wait" type — auto-advance when element found
        if (currentStep.type === "wait") {
          nextStepRef.current()
        }
        return
      }
      if (attempts++ < 80) { // poll up to 8 seconds
        pollRef.current = setTimeout(tryFind, 100)
      }
    }

    tryFind()
    return () => { cancelled = true; clearTimeout(pollRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id, location.pathname, isActive])

  // ── Update rect on scroll / resize ─────────────────────────────────────────
  useEffect(() => {
    if (!targetEl) return
    const update = () => setRect(targetEl.getBoundingClientRect())
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [targetEl])

  // ── Listen for click on target (action steps) ───────────────────────────────
  useEffect(() => {
    if (!targetEl || currentStep?.type !== "action") return

    const delay = currentStep.delay ?? 400
    let timer = null

    const handleClick = () => {
      timer = setTimeout(() => nextStepRef.current(), delay)
    }

    targetEl.addEventListener("click", handleClick, { once: true })
    return () => {
      targetEl.removeEventListener("click", handleClick)
      if (timer) clearTimeout(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEl, currentStep?.id])

  // ── Handle Next button ──────────────────────────────────────────────────────
  const handleNext = useCallback(async () => {
    if (!currentStep) return

    if (currentStep.type === "navigate") {
      setNavigating(true)
      nextStep()
      await new Promise(r => setTimeout(r, 80))
      navigate(currentStep.path)
      setNavigating(false)
      return
    }

    if (currentStep.type === "done") {
      endTutorial()
      return
    }

    nextStep()
  }, [currentStep, nextStep, endTutorial, navigate])

  // Don't render anything while invisible
  if (!isActive || !currentStep) return null
  // "wait" steps have no visual
  if (currentStep.type === "wait") return null

  return (
    <>
      {/* Inject keyframes once */}
      <style>{`
        @keyframes tutorialPulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(52,211,153,0.9), 0 0 20px 4px rgba(52,211,153,0.3); }
          50%       { box-shadow: 0 0 0 4px rgba(52,211,153,1),   0 0 28px 8px rgba(52,211,153,0.45); }
        }
      `}</style>

      <DarkPanels rect={rect} />
      <SpotlightRing rect={rect} />
      <Tooltip
        step={currentStep}
        steps={steps}
        stepIndex={active?.stepIndex ?? 0}
        rect={rect}
        onNext={handleNext}
        onPrev={prevStep}
        onSkip={endTutorial}
        navigating={navigating}
      />
    </>
  )
}
